/**
 * MockupPreviewPanel - Side panel for previewing and editing AI-created/edited UI mockups
 * Supports both preview mode (Sandpack) and code mode (Monaco), plus diff view for edits
 */
import React, { useState, useRef, useEffect, useCallback, useMemo, Component } from 'react';
import {
    X, Code, Eye, Check, Loader2, ExternalLink, Sparkles, Maximize2, Minimize2,
    GitCompare, Monitor, Tablet, Smartphone, Minus, Plus, RotateCcw, Clock, Tv
} from 'lucide-react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import Tip, { MOD } from '../ui/tip';
import { DiffEditor } from '@monaco-editor/react';
import MonacoEditor from '@monaco-editor/react';
import {
    SandpackProvider,
    SandpackLayout,
    SandpackPreview
} from "@codesandbox/sandpack-react";
import { createPortal } from 'react-dom';
import { atomDark } from "@codesandbox/sandpack-themes";
import { cn } from '../../lib/utils';
import { getMonacoLanguage } from '../../lib/fileTypes';
import { extractDependencies } from '../../lib/sandboxDeps';
import { useIsMobile } from '../../hooks/useIsMobile';

// Device presets for responsive testing
const DEVICE_PRESETS = [
    { id: 'responsive', label: 'Responsive', width: null, icon: Monitor },
    { id: 'desktop', label: 'Desktop', width: 1280, icon: Monitor },
    { id: 'tablet', label: 'Tablet', width: 768, icon: Tablet },
    { id: 'mobile', label: 'Mobile', width: 375, icon: Smartphone },
];

/**
 * Error boundary to catch Monaco DiffEditor disposal errors
 */
class DiffEditorErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        if (error?.message?.includes('TextModel got disposed') ||
            error?.message?.includes('DiffEditorWidget')) {
            return { hasError: false };
        }
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        if (!error?.message?.includes('TextModel got disposed') &&
            !error?.message?.includes('DiffEditorWidget')) {
            console.error('DiffEditor error:', error, errorInfo);
        }
    }

    render() {
        if (this.state.hasError) {
            return <div className="p-4 text-muted-foreground">Failed to load diff view</div>;
        }
        return this.props.children;
    }
}

// Zen Mode Full-Browser Overlay for Mockups
function ZenMockupOverlay({ file, currentDevice, devicePreset, onDeviceChange, zoom, onRefresh, onClose, dependencies, refreshKey }) {
    const [showToolbar, setShowToolbar] = useState(true);
    const timerRef = useRef(null);

    const handleMouseMove = useCallback(() => {
        setShowToolbar(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setShowToolbar(false);
        }, 2500);
    }, []);

    useEffect(() => {
        timerRef.current = setTimeout(() => {
            setShowToolbar(false);
        }, 2500);
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const zoomStyle = {
        zoom: zoom / 100,
        MozTransform: `scale(${zoom / 100})`,
        MozTransformOrigin: '0 0',
    };

    return createPortal(
        <div 
            className="fixed inset-0 z-[9999] bg-[#09090b] w-screen h-screen overflow-hidden flex flex-col font-sans select-none"
            onMouseMove={handleMouseMove}
        >
            {/* Floating Auto-Hiding Glassmorphic Toolbar */}
            <div 
                className={cn(
                    "absolute top-3 sm:top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 transform max-w-[calc(100vw-24px)]",
                    showToolbar ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"
                )}
            >
                <div className="flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-5 py-2 bg-black/90 backdrop-blur-2xl border border-white/15 rounded-full sm:rounded-2xl shadow-2xl w-full text-foreground">
                    {/* Title Badge */}
                    <div className="flex items-center gap-2 border-r border-white/10 pr-2 sm:pr-4 shrink-0">
                        <div className="h-7 w-7 rounded-lg bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                            <Tv className="h-3.5 w-3.5 text-violet-400" />
                        </div>
                        <div className="hidden sm:flex flex-col">
                            <span className="text-[11px] font-mono font-bold tracking-wider text-violet-400 uppercase leading-none truncate max-w-[120px]">
                                {file?.name || 'Mockup'}
                            </span>
                            <span className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                Zen Mode
                            </span>
                        </div>
                    </div>

                    {/* Device Presets */}
                    <div className="flex items-center gap-1 sm:gap-1.5 border-r border-white/10 pr-2 sm:pr-4">
                        {DEVICE_PRESETS.map((device, idx) => (
                            <Tip key={device.id} label={device.label} hint={`${idx + 1}`}>
                                <button
                                    onClick={() => onDeviceChange(device.id)}
                                    data-testid={`mockup-zen-device-${device.id}`}
                                    className={cn(
                                        "h-8 sm:h-7 px-2 sm:px-3 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 active:scale-95",
                                        devicePreset === device.id
                                            ? 'bg-violet-500/25 text-violet-300 border border-violet-500/40 shadow-sm shadow-violet-500/20'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent'
                                    )}
                                >
                                    <device.icon className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                                    <span className="hidden md:inline-block text-xs">{device.label.split(' ')[0]}</span>
                                    <span className="hidden lg:inline-block text-[10px] font-mono opacity-50">[{idx + 1}]</span>
                                </button>
                            </Tip>
                        ))}
                    </div>

                    {/* Zoom & Refresh */}
                    <div className="flex items-center gap-1 border-r border-white/10 pr-2 sm:pr-4 shrink-0">
                        <Tip label="Refresh preview" hint={`${MOD}R`}>
                            <button
                                onClick={onRefresh}
                                data-testid="mockup-zen-refresh"
                                className="h-8 w-8 sm:h-7 sm:w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/10 flex items-center justify-center transition-all active:scale-95"
                            >
                                <RotateCcw className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                            </button>
                        </Tip>
                        <span className="text-xs text-muted-foreground font-mono px-1.5 hidden sm:inline-block">
                            {zoom}%
                        </span>
                    </div>

                    {/* Exit Button & ESC Badge */}
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="hidden md:inline-block text-[10px] text-zinc-400 font-mono px-2 py-0.5 rounded bg-white/5 border border-white/10">
                            ESC
                        </span>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={onClose}
                            className="h-8 sm:h-7 px-2.5 sm:px-3 rounded-full sm:rounded-lg text-xs bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/30 flex items-center gap-1.5 transition-all active:scale-95"
                        >
                            <X className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                            <span className="hidden sm:inline-block">Exit</span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Sandpack Canvas */}
            <div className="flex-1 w-full h-full bg-[#0d0d0d] overflow-hidden flex justify-center">
                <div className="h-full w-full bg-[#0d0d0d] flex flex-col" style={zoomStyle}>
                    <SandpackProvider
                        key={refreshKey}
                        template="react"
                        theme={atomDark}
                        files={{ "/App.js": file.content }}
                        options={{ externalResources: ["https://cdn.tailwindcss.com"] }}
                        customSetup={{ dependencies }}
                        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                    >
                        <SandpackLayout style={{
                            border: 'none',
                            borderRadius: 0,
                            backgroundColor: 'transparent',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            <SandpackPreview
                                style={{ flex: 1 }}
                                showOpenInCodeSandbox={false}
                                showRefreshButton={false}
                            />
                        </SandpackLayout>
                    </SandpackProvider>
                </div>
            </div>
        </div>,
        document.body
    );
}

export function MockupPreviewPanel({
    file = null,                // { id, name, category, content }
    originalContent = null,     // For diff view
    isDiffMode = false,         // Show diff view
    editSummary = '',           // Summary of changes
    onContentChange,            // (fileId, content)
    onAcceptChanges,            // Accept diff changes
    onRejectChanges,            // Reject and close
    onOpenInEditor,             // Open in full editor
    onClose,
    isSaving = false,
    isAccepted = false,         // Changes already applied
    readOnly = false,           // Shared-link visitor: review only, nothing to apply
    onOpenLatest = null         // Switch to latest version
}) {
    const [viewMode, setViewMode] = useState('preview'); // 'preview' | 'code'
    const [zoom, setZoom] = useState(100);
    const [devicePreset, setDevicePreset] = useState('responsive');
    const [refreshKey, setRefreshKey] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const isMobile = useIsMobile();
    const [isZenMode, setIsZenMode] = useState(false);
    const diffEditorRef = useRef(null);

    const dependencies = useMemo(() => extractDependencies(file?.content), [file?.content]);
    const currentDevice = DEVICE_PRESETS.find(d => d.id === devicePreset);

    // Zoom handlers
    const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
    const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
    const handleRefresh = () => setRefreshKey(prev => prev + 1);

    // Keyboard Shortcuts Listener
    useEffect(() => {
        const handleKeyDown = (e) => {
            const activeEl = document.activeElement;
            const isInput = activeEl && (
                ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeEl.tagName) ||
                activeEl.isContentEditable ||
                activeEl.classList?.contains('monaco-editor')
            );

            // Cmd+Shift+F or Ctrl+Shift+F -> Toggle Zen mode
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'f') {
                e.preventDefault();
                setIsZenMode(prev => !prev);
                return;
            }

            // Esc -> Exit Zen Mode or Fullscreen Modal or Close Panel
            if (e.key === 'Escape') {
                if (isZenMode) {
                    setIsZenMode(false);
                    e.preventDefault();
                } else if (isFullscreen) {
                    setIsFullscreen(false);
                    e.preventDefault();
                }
                return;
            }

            // Intercept 1-4 / Cmd+R when zen mode, fullscreen, or not in text fields
            if (!isInput && (isZenMode || isFullscreen)) {
                if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'r') {
                    e.preventDefault();
                    handleRefresh();
                } else if (e.key === '1') {
                    setDevicePreset('responsive');
                } else if (e.key === '2') {
                    setDevicePreset('desktop');
                } else if (e.key === '3') {
                    setDevicePreset('tablet');
                } else if (e.key === '4') {
                    setDevicePreset('mobile');
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isZenMode, isFullscreen]);

    // Global error handler for Monaco
    useEffect(() => {
        const handleError = (event) => {
            if (event.message?.includes('TextModel got disposed') ||
                event.message?.includes('DiffEditorWidget')) {
                event.preventDefault();
                event.stopPropagation();
                return true;
            }
        };

        window.addEventListener('error', handleError, true);
        return () => window.removeEventListener('error', handleError, true);
    }, []);

    // Cleanup DiffEditor
    useEffect(() => {
        return () => {
            if (diffEditorRef.current) {
                try {
                    const editor = diffEditorRef.current;
                    editor.setModel?.(null);
                    editor.dispose?.();
                } catch (e) {
                    console.debug('DiffEditor cleanup:', e.message);
                }
                diffEditorRef.current = null;
            }
        };
    }, [isDiffMode]);

    const handleDiffEditorMount = useCallback((editor) => {
        diffEditorRef.current = editor;
    }, []);

    // A mockup only ever holds JSX, so an unrecognised extension stays
    // javascript rather than falling back to plaintext.
    const language = useMemo(() => getMonacoLanguage(file?.name, 'javascript'), [file?.name]);

    // Handle content change
    const handleCodeChange = (newContent) => {
        if (onContentChange && !isDiffMode) {
            onContentChange(file.id, newContent);
        }
    };

    if (!file) return null;

    // Frame style for device presets
    const getFrameStyle = () => {
        if (currentDevice?.width) {
            return {
                width: `${currentDevice.width}px`,
                maxWidth: '100%',
                margin: '0 auto',
            };
        }
        return { width: '100%', height: '100%' };
    };

    const zoomStyle = {
        zoom: zoom / 100,
        MozTransform: `scale(${zoom / 100})`,
        MozTransformOrigin: '0 0',
    };

    const PanelContent = (
        // Docked, the enclosing ArtifactSurface owns radius/border/background.
        // Fullscreen is the one case where this panel deliberately escapes its
        // surface, so it has to paint its own backdrop.
        <div className={cn(
            "flex min-h-0 flex-col overflow-hidden",
            isFullscreen ? "fixed inset-0 z-[100] bg-[#1e1e1e]" : "h-full"
        )}>
            {/* Header */}
            <div className={`flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/40 ${isMobile ? 'pt-safe-top' : ''}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0 border ${isDiffMode
                        ? 'bg-gradient-to-br from-yellow-500/30 to-orange-500/20 border-yellow-500/30'
                        : 'bg-gradient-to-br from-violet-500/30 to-purple-500/20 border-violet-500/30'
                        }`}>
                        {isDiffMode ? (
                            <GitCompare className="h-4 w-4 text-yellow-400" />
                        ) : (
                            <Sparkles className="h-4 w-4 text-violet-400" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold truncate flex items-center gap-2">
                            {file.name}
                            {isDiffMode && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                                    Review Changes
                                </span>
                            )}
                        </h3>
                        <div className="flex items-center gap-2 text-xs">
                            <span className="hidden sm:inline-block px-1.5 py-0.5 rounded-md border bg-violet-500/20 text-violet-400 border-violet-500/30">
                                Mockup
                            </span>
                            {isDiffMode ? (
                                <span className="text-yellow-400/70 truncate max-w-[150px] sm:max-w-[200px]">
                                    {editSummary}
                                </span>
                            ) : isSaving ? (
                                <span className="text-muted-foreground flex items-center gap-1 hidden sm:flex">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Saving...
                                </span>
                            ) : (
                                <span className="text-green-400/70 flex items-center gap-1 hidden sm:flex">
                                    <Check className="h-3 w-3" />
                                    Saved
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* View Mode Toggle & Actions */}
                <div className="flex items-center gap-2">
                    {!isDiffMode && (
                        <div className="flex items-center bg-white/5 rounded-lg p-0.5">
                            <button
                                onClick={() => setViewMode('preview')}
                                className={cn(
                                    "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all",
                                    viewMode === 'preview'
                                        ? "bg-violet-500/20 text-violet-400"
                                        : "text-muted-foreground hover:text-foreground"
                                )}
                            >
                                <Eye className="h-3.5 w-3.5" />
                                Preview
                            </button>
                            <button
                                onClick={() => setViewMode('code')}
                                className={cn(
                                    "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all",
                                    viewMode === 'code'
                                        ? "bg-violet-500/20 text-violet-400"
                                        : "text-muted-foreground hover:text-foreground"
                                )}
                            >
                                <Code className="h-3.5 w-3.5" />
                                Code
                            </button>
                        </div>
                    )}
                    {!isDiffMode && (
                        <>
                            <Tip label="Full browser window" hint={`${MOD}⇧F`}>
                                <button
                                    onClick={() => setIsZenMode(!isZenMode)}
                                    data-testid="mockup-zen"
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors text-violet-400"
                                >
                                    <Tv className="h-4 w-4" />
                                </button>
                            </Tip>
                            <Tip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen preview'}>
                                <button
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    data-testid="mockup-fullscreen"
                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                >
                                    {isFullscreen ? (
                                        <Minimize2 className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                        <Maximize2 className="h-4 w-4 text-muted-foreground" />
                                    )}
                                </button>
                            </Tip>
                        </>
                    )}
                    <Tip label={isDiffMode ? 'Discard changes' : 'Close'}>
                        <button
                            onClick={isDiffMode ? onRejectChanges : onClose}
                            data-testid="mockup-close"
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                            <X className="h-4 w-4 text-muted-foreground" />
                        </button>
                    </Tip>
                </div>
            </div>

            {/* History Warning Banner */}
            {file.id === 'history' && (
                <div className="flex items-center justify-between px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-amber-200/90 text-[11px] font-medium animate-in slide-in-from-top-2 duration-300">
                    <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-amber-400" />
                        <span>Viewing historical version. Edits won't be saved to the database.</span>
                    </div>
                    {onOpenLatest && (
                        <button
                            onClick={onOpenLatest}
                            className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 px-2 py-0.5 rounded border border-amber-500/30 transition-colors"
                        >
                            Open Latest Version
                        </button>
                    )}
                </div>
            )}

            {/* Preview Toolbar (only in preview mode) */}
            {viewMode === 'preview' && !isDiffMode && (
                <div className="flex items-center gap-2 px-3 py-2 bg-black/60 border-b border-white/5">
                    {/* Device Presets */}
                    <div className="flex items-center gap-1 border-r border-white/10 pr-3 mr-1">
                        {DEVICE_PRESETS.map((device, idx) => (
                            // The 1–4 and ⌘R bindings only exist in zen or
                            // fullscreen, so the hint appears only where the key
                            // actually does something.
                            <Tip key={device.id} label={device.label} hint={isFullscreen ? `${idx + 1}` : undefined}>
                                <button
                                    onClick={() => setDevicePreset(device.id)}
                                    data-testid={`mockup-device-${device.id}`}
                                    className={cn(
                                        "p-1.5 rounded-lg transition-all",
                                        devicePreset === device.id
                                            ? 'bg-violet-500/20 text-violet-400'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                                    )}
                                >
                                    <device.icon className="h-4 w-4" />
                                </button>
                            </Tip>
                        ))}
                    </div>

                    {/* Zoom Controls */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleZoomOut}
                            disabled={zoom <= 25}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 disabled:opacity-30 transition-all"
                        >
                            <Minus className="h-4 w-4" />
                        </button>
                        <div className="flex items-center gap-2 min-w-[100px]">
                            <Slider
                                value={[zoom]}
                                onValueChange={([val]) => setZoom(val)}
                                min={25}
                                max={200}
                                step={5}
                                className="w-16"
                            />
                            <span className="text-xs text-muted-foreground font-mono w-8">
                                {zoom}%
                            </span>
                        </div>
                        <button
                            onClick={handleZoomIn}
                            disabled={zoom >= 200}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 disabled:opacity-30 transition-all"
                        >
                            <Plus className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Refresh */}
                    <div className="ml-auto">
                        <Tip label="Refresh preview" hint={isFullscreen ? `${MOD}R` : undefined}>
                            <button
                                onClick={handleRefresh}
                                data-testid="mockup-refresh"
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all"
                            >
                                <RotateCcw className="h-4 w-4" />
                            </button>
                        </Tip>
                    </div>
                </div>
            )}

            {/* Content Area */}
            <div className="flex-1 overflow-hidden">
                {isDiffMode ? (
                    // Diff View (Code comparison)
                    <DiffEditorErrorBoundary>
                        <DiffEditor
                            key={`diff-${file.id}`}
                            original={originalContent || ''}
                            modified={file.content || ''}
                            language={language}
                            theme="vs-dark"
                            onMount={handleDiffEditorMount}
                            options={{
                                readOnly: true,
                                renderSideBySide: true,
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                fontSize: 13,
                                lineNumbers: 'on',
                                wordWrap: 'on',
                            }}
                        />
                    </DiffEditorErrorBoundary>
                ) : viewMode === 'preview' ? (
                    // Live Preview (Sandpack)
                    <div className="h-full w-full bg-[#0d0d0d] overflow-hidden flex justify-center">
                        {/* Responsive mode - full width, no frame */}
                        {devicePreset === 'responsive' ? (
                            <div className="h-full w-full bg-[#0d0d0d] flex flex-col" style={zoomStyle}>
                                <SandpackProvider
                                    key={refreshKey}
                                    template="react"
                                    theme={atomDark}
                                    files={{
                                        "/App.js": file.content,
                                    }}
                                    options={{
                                        externalResources: ["https://cdn.tailwindcss.com"],
                                    }}
                                    customSetup={{
                                        dependencies: dependencies
                                    }}
                                    style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                                >
                                    <SandpackLayout style={{
                                        border: 'none',
                                        borderRadius: 0,
                                        backgroundColor: 'transparent',
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}>
                                        <SandpackPreview
                                            style={{ flex: 1 }}
                                            showOpenInCodeSandbox={false}
                                            showRefreshButton={false}
                                        />
                                    </SandpackLayout>
                                </SandpackProvider>
                            </div>
                        ) : (
                            /* Device preset modes - show browser frame */
                            <div className="p-4">
                                <div
                                    className="bg-[#111] rounded-xl border border-white/10 overflow-hidden flex flex-col"
                                    style={{
                                        ...getFrameStyle(),
                                        height: 'fit-content',
                                        minHeight: '400px',
                                    }}
                                >
                                    {/* Browser-like header */}
                                    <div className="flex items-center gap-2 px-4 py-2 bg-black/50 border-b border-white/5">
                                        <div className="flex gap-1.5">
                                            <div className="w-3 h-3 rounded-full bg-red-500/60" />
                                            <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                                            <div className="w-3 h-3 rounded-full bg-green-500/60" />
                                        </div>
                                        <div className="flex-1 mx-4">
                                            <div className="bg-white/5 rounded-md px-3 py-1 text-xs text-muted-foreground font-mono text-center">
                                                localhost:3000
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sandpack Preview */}
                                    <div className="flex-1 overflow-auto origin-top-left" style={zoomStyle}>
                                        <SandpackProvider
                                            key={refreshKey}
                                            template="react"
                                            theme={atomDark}
                                            files={{
                                                "/App.js": file.content,
                                            }}
                                            options={{
                                                externalResources: ["https://cdn.tailwindcss.com"],
                                            }}
                                            customSetup={{
                                                dependencies: dependencies
                                            }}
                                        >
                                            <SandpackLayout style={{
                                                border: 'none',
                                                borderRadius: 0,
                                                backgroundColor: 'transparent',
                                                minHeight: '350px',
                                            }}>
                                                <SandpackPreview
                                                    style={{ minHeight: '350px' }}
                                                    showOpenInCodeSandbox={false}
                                                    showRefreshButton={false}
                                                />
                                            </SandpackLayout>
                                        </SandpackProvider>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    // Code Editor (Monaco)
                    <MonacoEditor
                        height="100%"
                        language={language}
                        value={file.content}
                        theme="vs-dark"
                        onChange={handleCodeChange}
                        options={{
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            fontSize: 13,
                            lineNumbers: 'on',
                            wordWrap: 'on',
                            tabSize: 2,
                        }}
                    />
                )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-2 border-t border-white/10 bg-black/40">
                {isDiffMode ? (
                    <>
                        <span className="text-xs text-yellow-400/70">
                            {readOnly
                                ? 'Read-only preview of the demo diff'
                                : isAccepted ? 'Changes have been applied' : 'Review the AI changes before applying'}
                        </span>
                        <div className="flex items-center gap-2">
                            {/* Nothing to accept on a shared link — the file is
                                not the visitor's to change. */}
                            {readOnly ? null : isAccepted ? (
                                <div className="flex items-center gap-1 px-3 h-7 rounded-lg bg-green-500/10 text-green-400 border border-green-500/20 text-xs font-medium">
                                    <Check className="h-3 w-3" />
                                    Applied
                                </div>
                            ) : (
                                <>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={onRejectChanges}
                                        className="h-7 px-3 rounded-lg text-xs hover:bg-red-500/20 hover:text-red-400"
                                    >
                                        <X className="h-3 w-3 mr-1.5" />
                                        Discard
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={onAcceptChanges}
                                        className="h-7 px-3 rounded-lg text-xs bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30"
                                    >
                                        <Check className="h-3 w-3 mr-1.5" />
                                        Accept Changes
                                    </Button>
                                </>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <span className="text-xs text-muted-foreground">
                            {viewMode === 'preview' ? 'Live preview • Switch to Code to edit' : 'Auto-saves as you type'}
                        </span>
                        <Button
                            size="sm"
                            onClick={onOpenInEditor}
                            className="h-7 px-3 rounded-lg text-xs bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 border border-violet-500/30"
                        >
                            <ExternalLink className="h-3 w-3 mr-1.5" />
                            Full Editor
                        </Button>
                    </>
                )}
            </div>
        </div>
    );

    if (isZenMode) {
        return (
            <ZenMockupOverlay
                file={file}
                currentDevice={currentDevice}
                devicePreset={devicePreset}
                onDeviceChange={setDevicePreset}
                zoom={zoom}
                onRefresh={handleRefresh}
                onClose={() => setIsZenMode(false)}
                dependencies={dependencies}
                refreshKey={refreshKey}
            />
        );
    }

    if (isFullscreen) {
        return createPortal(
            <>
                <div
                    className="fixed inset-0 bg-black/80 z-[99] backdrop-blur-sm"
                    onClick={() => setIsFullscreen(false)}
                />
                {PanelContent}
            </>,
            document.body
        );
    }

    return PanelContent;
}

export default MockupPreviewPanel;
